import { endpoint as apiEndpoint } from "@services/api.services";
import { useQueryClient } from "@tanstack/react-query";
import { decryptData, showToast } from "@utils/helper/index.helper";
import {
  useBlukDeleteData,
  useBulkBlogEdit,
  useDeleteData,
} from "@utils/query/index.query";
import { useSelectionStore } from "@utils/states/index.state";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

interface ActionParams {
  type:
    | "page-add"
    | "page-edit"
    | "page-view"
    | "page-close"
    | "drawer-view"
    | "modal-add"
    | "modal-view"
    | "modal-edit"
    | "modal-close"
    | "mode-add"
    | "mode-view"
    | "mode-edit"
    | "mode-close"
    | "close"
    | "delete"
    | "confirm-close"
    | "confirm"
    | "clear"
    | "filter"
    | "logout"
    | "record"
    | "view"
    | "bulk-edit";

  data?:
    | string
    | number
    | bigint
    | true
    | object
    | undefined
    | Array<any>
    | null
    | boolean;
}

const useHandleAction = ({
  refetch,
  endpoint,
  bulktype,
}: {
  refetch?: () => void;
  endpoint?: string;
  bulktype?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mutate, isPending: deletePending } = useDeleteData();

  const { selectedRows, clearSelection, selectedRowDetails, setIsFormPending } =
    useSelectionStore();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const { mutate: bulkDeleteMutate, isPending: blukPending } =
    useBlukDeleteData();

  const { mutate: BulkEditMutation, isPending: bulkEditiPending } =
    useBulkBlogEdit();
  const handleAction = ({ type, data }: ActionParams) => {
    switch (type) {
      case "page-add":
        searchParams.set("page", "add");
        break;

      case "page-view":
      case "page-edit":
        searchParams.set("page", type === "page-view" ? "view" : "edit");
        searchParams.set(
          "page-item",
          ((typeof data === "number" || typeof data === "string") &&
            data?.toString()) ||
            ""
        );
        break;

      case "page-close":
        searchParams.delete("page");
        searchParams.delete("page-item");
        searchParams.delete("tab");
        break;

      case "drawer-view":
        searchParams.delete("page");
        searchParams.delete("page-item");
        searchParams.delete("modal");
        searchParams.delete("modal-item");
        searchParams.delete("confirm");
        searchParams.delete("confirm-item");
        searchParams.set("drawer", "view");
        searchParams.set(
          "drawer-item",
          ((typeof data === "number" || typeof data === "string") &&
            data?.toString()) ||
            ""
        );
        break;

      case "modal-add":
        searchParams.set("modal", "add");
        break;

      case "modal-view":
      case "modal-edit":
        searchParams.set("modal", type === "modal-view" ? "view" : "edit");
        searchParams.set(
          "modal-item",
          ((typeof data === "number" || typeof data === "string") &&
            data?.toString()) ||
            ""
        );
        break;

      case "modal-close":
        searchParams.delete("modal");
        searchParams.delete("modal-item");
        searchParams.delete("tab");
        break;

      case "mode-add":
        if (typeof data === "object" && data !== null && "type" in data) {
          searchParams.set("mode", (data as { type?: string; id?: string }).type || "");
          (data as { type?: string; id?: string }).id &&
            searchParams.set(
              "mode-add-item",
              (data as { type?: string; id?: string }).id || ""
            );
        } else {
          searchParams.set("mode", "");
        }
        break;

      case "mode-edit":
        if (
          typeof data === "object" &&
          data !== null &&
          ("type" in data || "id" in data)
        ) {
          const typedData = data as { type?: string; id?: number };
          searchParams.set("mode", typedData.type || "");
          searchParams.set("item", typedData.id?.toString() || "");
        } else {
          searchParams.set("mode", "");
          searchParams.set("item", "");
        }
        break;

      case "mode-view":
        searchParams.set("mode", "view");
        if (typeof data === "object" && data !== null && "id" in data) {
          searchParams.set("mode-item", (data as { id?: string }).id || "");
        } else {
          searchParams.set("mode-item", "");
        }
        break;

      case "mode-close":
        searchParams.delete("mode");
        searchParams.delete("mode-add-item");
        searchParams.delete("item");
        break;

      case "confirm":
        if (typeof data === "object" && data !== null) {
          const obj = data as {
            type?: string;
            id?: string | number;
            data?: string | number;
          };
          searchParams.delete("page");
          searchParams.delete("page-item");
          searchParams.delete("modal");
          searchParams.delete("modal-item");
          searchParams.delete("drawer");
          searchParams.delete("drawer-item");
          if (obj.type) searchParams.set("confirm", obj.type);
          if (obj.id || obj.data)
            searchParams.set(
              "confirm-item",
              (obj.id || obj.data)?.toString() || ""
            );
        }
        break;

      case "confirm-close":
        searchParams.delete("confirm");
        searchParams.delete("confirm-item");
        break;

      case "close":
        searchParams.delete("page");
        searchParams.delete("modal");
        searchParams.delete("mode");
        searchParams.delete("drawer");
        searchParams.delete("drawer-item");
        searchParams.delete("tab");
        searchParams.delete("confirm");
        searchParams.delete("item");
        searchParams.delete("confirm-item");
        setSearchParams(searchParams);
        break;

      case "clear":
        clearSelection();
        break;

      case "delete": {
        const isBulk = searchParams.get("confirm") === "bulk";
        const mutationConfig = {
          onSuccess: (response: any) => {
            if ([204, 200].includes(response.status)) {
              queryClient.invalidateQueries({
                queryKey: [`${pathname}-listing-scroll`],
              });
              queryClient.invalidateQueries({
                queryKey: [`${pathname}-listing`],
              });
              setIsFormPending(false);
              handleAction({ type: "close" });
              showToast({
                description: `The item has been deleted successfully`,
                type: "success",
                title: "Success",
              });
              if (typeof refetch === "function") {
                refetch();
              } else if (
                Array.isArray(refetch) ||
                typeof refetch === "string"
              ) {
                queryClient.invalidateQueries(refetch);
              }
              clearSelection();
            } else {
              Object.entries(response.response.data).forEach(([_, value]) => {
                showToast({
                  description: (value as string) ?? "Failed to delete",
                  type: "error",
                  title: "Error",
                });
              });
            }
          },
          onError: (data: any) => {
            setIsFormPending(false);
            Object.entries(data?.data || data?.response?.data || {}).forEach(
              ([key, value]) => {
                showToast({
                  description: (value as string) ?? "Failed to delete",
                  type: "error",
                  title: "Error",
                });
              }
            );
          },
        };

        if (isBulk) {
          bulkDeleteMutate(
            {
              data: { delete_ids: selectedRows, type: bulktype },
              endpoint: apiEndpoint["bulk-delete"],
            },
            mutationConfig
          );
        } else {
          mutate(
            {
              id: searchParams.get("confirm-item"),
              endpoint,
            },
            mutationConfig
          );
        }
        return;
      }

      case "bulk-edit": {
        const mutateConfiguration = {
          onSuccess: (response: any) => {
            if ([204, 207, 200].includes(response.status)) {
              console.log(
                "THIS INVALIDATE RAN AFTER BULK DELETE",
                `${pathname}-listing-scroll`
              );
              queryClient.invalidateQueries({
                queryKey: [`${pathname}-listing-scroll`],
              });
              handleAction({ type: "close" });
              showToast({
                description: `The item has been moved to deleted successfully`,
                type: "success",
                title: "Success",
              });
              if (typeof refetch === "function") {
                refetch();
              } else if (
                Array.isArray(refetch) ||
                typeof refetch === "string"
              ) {
                queryClient.invalidateQueries(refetch);
              }
              clearSelection();
            } else {
              Object.entries(response.response.data).forEach(([_, value]) => {
                showToast({
                  description: (value as string) ?? "Failed to move to delete",
                  type: "error",
                  title: "Error",
                });
              });
            }
          },
          onError: (data: any) => {
            Object.entries(data?.response?.data || {}).forEach(
              ([key, value]) => {
                showToast({
                  description: (value as string) ?? "Failed to delete" + key,
                  type: "error",
                  title: "Error",
                });
              }
            );
          },
        };

        const payload = selectedRowDetails.map((item) => ({
          is_deleted: true,
          status: "deleted",
          id: item?.slug,
        }));
        console.log("Payload", selectedRowDetails);
        BulkEditMutation(
          {
            data: payload,
            endpoint: apiEndpoint["bulk-update"],
          },
          mutateConfiguration
        );
        return;
      }

      default:
        break;
    }

    setSearchParams(searchParams);
  };

  return {
    searchParams,
    setSearchParams,
    handleAction,
    deletePending,
    blukPending,
  };
};

const useNetwork = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline };
};

const useLog = () => {
  const isRemember = Cookies.get("remember") === "true";
  let data: Record<string, any> = {
    full_name: "",
    access: "",
    refresh: "",
    email: "",
    image: "",
    userId: "",
    collegeId: false,
  };

  const clear = () => {
    sessionStorage.clear();
    Cookies.remove("access");
    Cookies.remove("refresh");
    Cookies.remove("full_name");
    Cookies.remove("email");
    Cookies.remove("image");
    Cookies.remove("userId");
    data = {
      full_name: "",
      access: "",
      refresh: "",
      email: "",
      image: "",
      userId: "",
    };
  };

  if (isRemember) {
    data = {
      access: Cookies.get("access") || "",
      refresh: Cookies.get("refresh") || "",
      full_name: Cookies.get("full_name") || "",
      email: Cookies.get("email") || "",
      image: Cookies.get("image") || "",
      userId: Cookies.get("userId") || "",
      collegeId: Cookies.get("collegeId") ?? false,
    };
  } else {
    data = {
      access: sessionStorage.getItem("access") || "",
      refresh: sessionStorage.getItem("refresh") || "",
      full_name: sessionStorage.getItem("full_name") || "",
      email: sessionStorage.getItem("email") || "",
      image: sessionStorage.getItem("image") || "",
      userId: sessionStorage.getItem("userId") || "",
      collegeId: sessionStorage.getItem("collegeId") ?? false,
    };
  }

  return {
    access: data.access,
    refresh: data.refresh,
    full_name: decryptData(data?.full_name),
    email: decryptData(data?.email),
    image: decryptData(data?.image || ""),
    userId: decryptData(data?.userId),
    isRemember,
    collegeId: data.collegeId,
    clear,
  };
};

const usePathname = () => {
  const location = useLocation();
  let data = location.pathname?.split("/");
  if (data.length > 2) return data.slice(1).join("-");
  return data[1] ?? "";
};

export { useHandleAction, useLog, useNetwork, usePathname };
